import { ChangeDetectionStrategy, Component, computed, inject, Input } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { QuoteControllerSelectors } from 'sharedStores/quote-controller/quote-controller.module';
import { QuoteSelectors } from 'stores/quote/quote.module';
import { selectQuoteState } from 'stores/quote/quote.selectors';
import { AppConfigService } from 'sharedServices/app-config.service';
import { BrandNames } from 'shared/base/generics/enums/brand-names';
import { SkeletonLoaderDirective } from 'sharedDirectives/skeleton-loader.directive';
import { AsyncPipe, CurrencyPipe, NgClass } from '@angular/common';
import { PremiumDifference } from 'sharedModels/premium-difference';
import { Observable } from 'rxjs';
import { QuoteFlowType } from 'sharedEnums/quote-flow-type';

@Component({
    selector: 'premium-banner-widget',
    templateUrl: './html/premium-banner-widget.component.html',
    styleUrls: ['./css/premium-banner-widget.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [SkeletonLoaderDirective, AsyncPipe, CurrencyPipe, NgClass],
    host: { ngSkipHydration: 'true' },
})
@UntilDestroy()
// eslint-disable-next-line @angular-eslint/directive-class-suffix
export class PremiumBannerWidgetComponent {
    @Input() styles: { premiumTextClass: string; loaderHeight: string };
    @Input() primaryColour: string;
    @Input() discountColour: string;
    @Input() showDiscount: boolean;
    @Input() premium: PremiumDifference;
    @Input() isVariant: boolean = false;

    public quoteFlowType = this.store$.selectSignal(QuoteSelectors.selectQuoteFlowType);
    public premiumDifference = this.isVehicleFlow
        ? this.store$.selectSignal(QuoteSelectors.selectVehiclePremium())
        : this.store$.selectSignal(QuoteSelectors.selectHomeContentsPremium());
    public isLoading = this.store$.selectSignal(QuoteSelectors.selectIsPremiumLoading);
    public premiumWidget$: Observable<any>;
    public displayCallCenterPremium = this.store$.selectSignal(
        QuoteSelectors.displayCallCenterPremium,
    );
    public hasOnlineDiscount = this.store$.selectSignal(QuoteSelectors.hasOnlineDiscount);
    private readonly appConfig = inject(AppConfigService);
    private readonly isEmbeddedCashbackBrand = this.store$.selectSignal(
        QuoteSelectors.showEmbeddedCashback,
    );
    private readonly isVirsekerBrand = this.appConfig.config().brand === BrandNames.VIRSEKER;
    private readonly quoteState = this.store$.selectSignal(selectQuoteState);
    private readonly vehiclePremiumDisplay = this.store$.selectSignal(
        QuoteSelectors.selectVehiclePremiumDisplay(),
    );
    private readonly homeContentsPremiumDisplay = this.store$.selectSignal(
        QuoteSelectors.selectHomeContentsPremiumDisplay(),
    );
    // For embedded cashback brands: skip doManualCalculations to avoid double-counting.
    // Two API response shapes exist:
    //   - Initial load (GET_QUOTE): premium = base only, onlinePremium = full (incl. SASRIA/optionals)
    //   - After recalc (addUpdateHomeContents/getCashBack): premium = full, onlinePremium = 0
    // Using onlinePremium || premium handles both shapes for vehicle and home contents flows.
    public premiumDisplay = computed(() => {
        if (this.isEmbeddedCashbackBrand() || this.isVirsekerBrand)
            return this.isVehicleFlow
                ? this.quoteState().newPremium
                : this.quoteState().onlinePremium || this.quoteState().premium;
        if (this.isVehicleFlow) return this.vehiclePremiumDisplay();
        return this.homeContentsPremiumDisplay();
    });

    constructor(public store$: Store) {
        this.premiumWidget$ = this.store$.select(
            QuoteControllerSelectors.selectWidgetByName('premium-view-widget'),
        );
    }

    get isVehicleFlow(): boolean {
        return this.quoteFlowType() === QuoteFlowType.Vehicle;
    }
}
